import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { auth, db } from '../firebase';
import MessageRequests from './MessageRequests';
import useChat from '../hooks/useChat';
import useToast from '../hooks/useToast';
import { createMessage } from '../services/messageService';
import { getUserById } from '../services/userService';
import ChatHeader from './ChatHeader';
import ChatBubble from './ChatBubble';
import './Messages.css';

const Messages = () => {
    const [activeTab, setActiveTab] = useState('inbox');
    const [inboxMessages, setInboxMessages] = useState([]);
    const [conversationsWithUsers, setConversationsWithUsers] = useState([]);
    const [currentUid, setCurrentUid] = useState(() => auth.currentUser?.uid || null);
    const [isArtist, setIsArtist] = useState(false);
    const [otherUserId, setOtherUserId] = useState(null);
    const [otherUser, setOtherUser] = useState(null);
    const [sending, setSending] = useState(false);
    const [draft, setDraft] = useState('');
    const [optimisticMessages, setOptimisticMessages] = useState([]);
    const { toast, showToast } = useToast();
    const location = useLocation();
    const navigate = useNavigate();
    const chatBodyRef = useRef(null);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            setCurrentUid(user?.uid || null);
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (!currentUid) {
            setIsArtist(false);
            return () => {};
        }
        const uref = doc(db, 'users', currentUid);
        const unsub = onSnapshot(
            uref,
            (snap) => {
                const role = snap.exists() ? snap.data()?.role || '' : '';
                setIsArtist(role === 'artist');
            },
            (err) => {
                console.error('User role snapshot error', err);
                setIsArtist(false);
            }
        );
        return () => unsub();
    }, [currentUid]);

    useEffect(() => {
        if (!isArtist && activeTab === 'requests') {
            setActiveTab('inbox');
        }
    }, [isArtist, activeTab]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const toParam = params.get('to');
        setOtherUserId(toParam || null);
    }, [location.search]);

    useEffect(() => {
        if (!otherUserId) {
            setOtherUser(null);
            return () => {};
        }
        (async () => {
            try {
                const profile = await getUserById(otherUserId);
                setOtherUser(profile);
            } catch (err) {
                console.error('Failed to load user for chat', err);
                setOtherUser(null);
            }
        })();
        return () => {};
    }, [otherUserId]);

    useEffect(() => {
        if (!currentUid) {
            setInboxMessages([]);
            return () => {};
        }
        
        // Get all messages where user is either sender or receiver
        const qSent = query(collection(db, 'messages'), where('senderId', '==', currentUid));
        const qReceived = query(collection(db, 'messages'), where('receiverId', '==', currentUid));
        
        const allMessagesMap = new Map();
        
        const updateInbox = () => {
            // Group messages by conversation partner (otherUserId)
            const conversationsMap = new Map();
            
            allMessagesMap.forEach((msg) => {
                const otherUserId = msg.senderId === currentUid ? msg.receiverId : msg.senderId;
                if (!otherUserId || otherUserId === currentUid) return;
                
                const existing = conversationsMap.get(otherUserId);
                const msgTime = msg.timestamp?.toMillis ? msg.timestamp.toMillis() : (msg.timestamp?.seconds ? msg.timestamp.seconds * 1000 : new Date(msg.timestamp || 0).getTime());
                
                if (!existing || msgTime > existing.latestTime) {
                    conversationsMap.set(otherUserId, {
                        otherUserId,
                        latestMessage: msg,
                        latestTime: msgTime,
                        senderName: msg.senderId === currentUid ? 'You' : (msg.senderName || 'User'),
                        senderId: msg.senderId,
                        receiverId: msg.receiverId,
                        text: msg.text,
                        timestamp: msg.timestamp,
                    });
                }
            });
            
            // Convert to array and sort by latest message time
            const conversations = Array.from(conversationsMap.values()).sort((a, b) => b.latestTime - a.latestTime);
            setInboxMessages(conversations);
        };
        
        const unsubSent = onSnapshot(
            qSent,
            (snap) => {
                snap.docs.forEach((d) => {
                    allMessagesMap.set(d.id, { id: d.id, ...d.data() });
                });
                updateInbox();
            },
            (err) => {
                console.error('Inbox sent messages listener error', err);
            }
        );
        
        const unsubReceived = onSnapshot(
            qReceived,
            (snap) => {
                snap.docs.forEach((d) => {
                    allMessagesMap.set(d.id, { id: d.id, ...d.data() });
                });
                updateInbox();
            },
            (err) => {
                console.error('Inbox received messages listener error', err);
            }
        );
        
        return () => {
            unsubSent();
            unsubReceived();
        };
    }, [currentUid]);

    const { messages: chatMessages = [], loading: chatLoading } = useChat(currentUid, otherUserId);
    
    // Merge optimistic messages with real messages (optimistic messages will be replaced when real ones arrive)
    const displayMessages = useMemo(() => {
        const realMessageIds = new Set(chatMessages.map(m => m.id));
        const validOptimistic = optimisticMessages.filter(opt => !realMessageIds.has(opt.id));
        return [...chatMessages, ...validOptimistic].sort((a, b) => {
            const ta = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp?.seconds ? a.timestamp.seconds * 1000 : new Date(a.timestamp || 0).getTime());
            const tb = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp?.seconds ? b.timestamp.seconds * 1000 : new Date(b.timestamp || 0).getTime());
            return ta - tb;
        });
    }, [chatMessages, optimisticMessages]);

    const scrollToBottom = useCallback(() => {
        if (chatBodyRef.current) {
            requestAnimationFrame(() => {
                chatBodyRef.current.scrollTo({
                    top: chatBodyRef.current.scrollHeight,
                    behavior: 'smooth'
                });
            });
        }
    }, []);

    useEffect(() => {
        scrollToBottom();
    }, [displayMessages, scrollToBottom]);

    const formatDate = (timestamp) => {
        if (!timestamp) return '';
        if (timestamp.toDate) {
            return timestamp.toDate().toLocaleString();
        }
        return new Date(timestamp).toLocaleString();
    };

    const formatBubbleTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    };

    const handleSendMessage = async (text) => {
        if (!otherUserId) {
            throw new Error('Select someone to chat with first.');
        }
        
        // Turant optimistic message add karo (WhatsApp style)
        const tempId = `temp-${Date.now()}-${Math.random()}`;
        const optimisticMsg = {
            id: tempId,
            text: text.trim(),
            senderId: currentUid,
            receiverId: otherUserId,
            timestamp: { toMillis: () => Date.now(), toDate: () => new Date() },
            isOptimistic: true
        };
        setOptimisticMessages(prev => [...prev, optimisticMsg]);
        setSending(true);
        
        // Background mein actual message save karo (non-blocking)
        createMessage({ text, receiverId: otherUserId })
            .then(result => {
                if (result?.type === 'request') {
                    showToast('You need to tip this artist before sending messages.');
                    // Remove optimistic message if it was a request
                    setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
                } else {
                    // Optimistic message will be replaced by real message from subscription
                    // Remove it after a short delay to ensure real message arrived
                    setTimeout(() => {
                        setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
                    }, 1000);
                }
            })
            .catch(err => {
                console.error('Failed to send message', err);
                // Remove optimistic message on error
                setOptimisticMessages(prev => prev.filter(m => m.id !== tempId));
                showToast(err?.message || 'Failed to send message');
                throw err;
            })
            .finally(() => {
                setSending(false);
            });
    };

    const handleSelectConversation = (userId) => {
        if (!userId) return;
        const params = new URLSearchParams(location.search);
        params.set('to', userId);
        navigate(`/messages?${params.toString()}`, { replace: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const value = draft.trim();
        if (!value || sending) return;
        try {
            await handleSendMessage(value);
            setDraft('');
            scrollToBottom();
        } catch {
            // handled upstream
        }
    };

    const handleEnterKey = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    const chatHeaderName = useMemo(() => {
        if (otherUser?.name) return otherUser.name;
        if (otherUser?.displayName) return otherUser.displayName;
        if (otherUserId) return otherUserId;
        return 'Select a user';
    }, [otherUser, otherUserId]);

    const badgeLabel =
        otherUser?.badgeTitle ||
        otherUser?.badge ||
        (otherUser?.role ? `${otherUser.role.charAt(0).toUpperCase()}${otherUser.role.slice(1)} Badge` : null);

    // Load user details for inbox conversations
    useEffect(() => {
        if (inboxMessages.length === 0) {
            setConversationsWithUsers([]);
            return () => {};
        }
        
        (async () => {
            try {
                const enriched = await Promise.all(
                    inboxMessages.map(async (conv) => {
                        try {
                            const user = await getUserById(conv.otherUserId);
                            return {
                                ...conv,
                                userName: user?.name || user?.displayName || 'User',
                                userPhoto: user?.photoURL || null,
                            };
                        } catch {
                            return {
                                ...conv,
                                userName: conv.otherUserId || 'User',
                                userPhoto: null,
                            };
                        }
                    })
                );
                setConversationsWithUsers(enriched);
            } catch (err) {
                console.error('Failed to load user details for inbox', err);
                setConversationsWithUsers(inboxMessages);
            }
        })();
        
        return () => {};
    }, [inboxMessages, currentUid]);

    const renderInboxList = () => (
        <div className="chat-placeholder">
            {conversationsWithUsers.length === 0 ? (
                <div className="placeholder-empty">No conversations yet</div>
            ) : (
                conversationsWithUsers.map((conv) => (
                    <button
                        key={conv.otherUserId}
                        className="inbox-item"
                        onClick={() => handleSelectConversation(conv.otherUserId)}
                    >
                        <div className="inbox-avatar">
                            {conv.userPhoto ? (
                                <img src={conv.userPhoto} alt={conv.userName} className="w-full h-full object-cover rounded-full" />
                            ) : (
                                <span>{(conv.userName || 'U').toString().charAt(0).toUpperCase()}</span>
                            )}
                        </div>
                        <div className="inbox-content">
                            <div className="inbox-header">
                                <h3>{conv.userName}</h3>
                                <span>{formatDate(conv.timestamp)}</span>
                            </div>
                            <p>{conv.senderId === currentUid ? `You: ${conv.text}` : conv.text}</p>
                        </div>
                    </button>
                ))
            )}
        </div>
    );

    const renderChat = () => {
        if (!otherUserId) {
            return renderInboxList();
        }

        return (
            <div className="chat-panel">
                <ChatHeader
                    name={chatHeaderName}
                    badge={badgeLabel}
                    avatarUrl={otherUser?.photoURL}
                    onBack={() => navigate('/messages')}
                />
                <div className="chat-body" ref={chatBodyRef}>
                    {chatLoading ? (
                        <div className="chat-empty">Loading conversation...</div>
                    ) : displayMessages.length === 0 ? (
                        <div className="chat-empty">No messages yet. Say hi!</div>
                    ) : (
                        displayMessages.map((msg, index) => {
                            const isOwn = msg.senderId === currentUid;
                            const prev = displayMessages[index - 1];
                            const showAvatar = !isOwn && (!prev || prev.senderId !== msg.senderId || 
                                (prev.timestamp && msg.timestamp && 
                                 Math.abs((prev.timestamp.toMillis ? prev.timestamp.toMillis() : new Date(prev.timestamp).getTime()) - 
                                         (msg.timestamp.toMillis ? msg.timestamp.toMillis() : new Date(msg.timestamp).getTime())) > 300000));
                            return (
                                <ChatBubble
                                    key={msg.id}
                                    text={msg.text}
                                    isOwn={isOwn}
                                    timestamp={formatBubbleTime(msg.timestamp)}
                                    showAvatar={showAvatar}
                                    avatarUrl={otherUser?.photoURL}
                                    senderInitial={(otherUser?.name || otherUser?.displayName || 'U')
                                        .toString()
                                        .charAt(0)
                                        .toUpperCase()}
                                    tipLabel={msg.tipId ? 'Tip verified' : null}
                                />
                            );
                        })
                    )}
                </div>
                <form className="chat-input-bar" onSubmit={handleSubmit}>
                    <input
                        type="text"
                        placeholder="Type a message"
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={handleEnterKey}
                        disabled={!otherUserId || sending}
                        className="chat-input"
                        autoComplete="off"
                    />
                    <button
                        type="submit"
                        className="chat-send-btn"
                        disabled={!otherUserId || sending || !draft.trim()}
                        aria-label="Send message"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="w-5 h-5"
                        >
                            <line x1="22" y1="2" x2="11" y2="13"></line>
                            <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                        </svg>
                    </button>
                </form>
            </div>
        );
    };

    return (
        <div className="messages-screen">
            {isArtist && (
                <div className="messages-tabs">
                    <button
                        onClick={() => setActiveTab('inbox')}
                        className={`tab-btn ${activeTab === 'inbox' ? 'active' : ''}`}
                    >
                        Inbox
                    </button>
                    <button
                        onClick={() => setActiveTab('requests')}
                        className={`tab-btn ${activeTab === 'requests' ? 'active' : ''}`}
                    >
                        Requests
                    </button>
                </div>
            )}

            <div className="messages-main">
                {isArtist && activeTab === 'requests' ? (
                    <div className="requests-panel">
                        <MessageRequests />
                    </div>
                ) : (
                    renderChat()
                )}
            </div>
            {toast && <div className="app-toast">{toast}</div>}
        </div>
    );
};

export default Messages;

